// MenuBar.js
import React, { useState, useRef, } from 'react';
import { useCurrentEditor } from '@tiptap/react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TableChartIcon from '@mui/icons-material/TableChart';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import CodeIcon from '@mui/icons-material/Code';
import HighlightIcon from '@mui/icons-material/Highlight';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import HIcon from '@mui/icons-material/HMobiledata';
import AddLinkIcon from '@mui/icons-material/AddLink';
import YouTubeIcon from '@mui/icons-material/YouTube';

const MenuBar = ({ editor }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [headingAnchorEl, setHeadingAnchorEl] = useState(null);
  const colorInputRef = useRef(null);

  if (!editor) {
    return null;
  }

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleHeadingMenuClick = (event) => {
    setHeadingAnchorEl(event.currentTarget);
  };

  const handleHeadingMenuClose = () => {
    setHeadingAnchorEl(null);
  };

  const handleColorClick = () => {
    colorInputRef.current.click();
  };

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('URL', previousUrl)

    // cancelled
    if (url === null) {
      return
    }

    // empty
    else if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink()
        .run()

      return
    }

    // update link
    else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url })
        .run()
    }
  }

  const addYoutubeVideo = () => {
    const url = prompt('Enter YouTube URL');

    if (url) {
      editor.commands.setYoutubeVideo({
        src: url,
        width: 640,
        height: 360,
      });
    }
  };

  return (
    <div style={{
      backgroundColor: '#eee',
      position: 'relative',
      top: 'auto',
      zIndex: 1000,
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 2,
      padding: '4px 6px',
    }}>
      <Tooltip title="Headings">
        <IconButton onClick={handleHeadingMenuClick}>
          <HIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={headingAnchorEl} open={Boolean(headingAnchorEl)} onClose={handleHeadingMenuClose}>
        <MenuItem
          onClick={() => {
            editor.chain().focus().toggleHeading({ level: 1 }).run();
            handleHeadingMenuClose();
          }}
          disabled={!editor.can().chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <Tooltip title="Heading 1">
            <IconButton color={editor.isActive('heading', { level: 1 }) ? 'primary' : 'default'}>
              <LooksOneIcon />
            </IconButton>
          </Tooltip>
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().toggleHeading({ level: 2 }).run();
            handleHeadingMenuClose();
          }}
          disabled={!editor.can().chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Tooltip title="Heading 2">
            <IconButton color={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}>
              <LooksTwoIcon />
            </IconButton>
          </Tooltip>
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().toggleHeading({ level: 3 }).run();
            handleHeadingMenuClose();
          }}
          disabled={!editor.can().chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Tooltip title="Heading 3">
            <IconButton color={editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'}>
              <Looks3Icon />
            </IconButton>
          </Tooltip>
        </MenuItem>
      </Menu>
      <Tooltip title="Bold">
        <IconButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          color={editor.isActive('bold') ? 'primary' : 'default'}
        >
          <FormatBoldIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Italic">
        <IconButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          color={editor.isActive('italic') ? 'primary' : 'default'}
        >
          <FormatItalicIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Underline">
        <IconButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          color={editor.isActive('underline') ? 'primary' : 'default'}
        >
          <FormatUnderlinedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Blockquote">
        <IconButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          disabled={!editor.can().chain().focus().toggleBlockquote().run()}
          color={editor.isActive('blockquote') ? 'primary' : 'default'}
        >
          <FormatIndentIncreaseIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Strikethrough">
        <IconButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          color={editor.isActive('strike') ? 'primary' : 'default'}
        >
          <StrikethroughSIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Code">
        <IconButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          disabled={!editor.can().chain().focus().toggleCode().run()}
          color={editor.isActive('code') ? 'primary' : 'default'}
        >
          <CodeIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Highlight">
        <IconButton
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          disabled={!editor.can().chain().focus().toggleHighlight().run()}
          color={editor.isActive('highlight') ? 'primary' : 'default'}
        >
          <HighlightIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Colors">
        <IconButton onClick={handleColorClick}>
          <ColorLensIcon />
        </IconButton>
        <input
          type="color"
          ref={colorInputRef}
          onInput={(e) => editor.chain().focus().setColor(e.target.value).run()}
          value={editor.getAttributes('textStyle').color || '#000000'}
          style={{ display: 'none' }}
        />
      </Tooltip>
      <Tooltip title="Align Left">
        <IconButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          disabled={!editor.can().chain().focus().unsetTextAlign().run()}
          color={editor.isActive('left') ? 'primary' : 'default'}
        >
          <FormatAlignLeftIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Align Center">
        <IconButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          disabled={!editor.can().chain().focus().unsetTextAlign().run()}
          color={editor.isActive('center') ? 'primary' : 'default'}
        >
          <FormatAlignCenterIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Align Right">
        <IconButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          disabled={!editor.can().chain().focus().unsetTextAlign().run()}
          color={editor.isActive('right') ? 'primary' : 'default'}
        >
          <FormatAlignRightIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Justify">
        <IconButton
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          disabled={!editor.can().chain().focus().unsetTextAlign().run()}
          color={editor.isActive('justify') ? 'primary' : 'default'}
        >
          <FormatAlignJustifyIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Bullet List">
        <IconButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={!editor.can().chain().focus().toggleBulletList().run()}
          color={editor.isActive('bulletList') ? 'primary' : 'default'}
        >
          <FormatListBulletedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Numbered List">
        <IconButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={!editor.can().chain().focus().toggleOrderedList().run()}
          color={editor.isActive('orderedList') ? 'primary' : 'default'}
        >
          <FormatListNumberedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Divider">
        <IconButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          disabled={!editor.can().chain().focus().setHorizontalRule().run()}
          color={editor.isActive('divider') ? 'primary' : 'default'}
        >
          <HorizontalRuleIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Table Options">
        <IconButton onClick={handleMenuClick}>
          <TableChartIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
            handleMenuClose();
          }}
        >
          Insert Table
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().deleteTable().run();
            handleMenuClose();
          }}
        >
          Delete Table
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().addColumnBefore().run();
            handleMenuClose();
          }}
          disabled={!editor.can().addColumnBefore()}
        >
          Add Column Before
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().addColumnAfter().run();
            handleMenuClose();
          }}
          disabled={!editor.can().addColumnAfter()}
        >
          Add Column After
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().deleteColumn().run();
            handleMenuClose();
          }}
          disabled={!editor.can().deleteColumn()}
        >
          Delete Column
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().addRowBefore().run();
            handleMenuClose();
          }}
          disabled={!editor.can().addRowBefore()}
        >
          Add Row Before
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().addRowAfter().run();
            handleMenuClose();
          }}
          disabled={!editor.can().addRowAfter()}
        >
          Add Row After
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().deleteRow().run();
            handleMenuClose();
          }}
          disabled={!editor.can().deleteRow()}
        >
          Delete Row
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().toggleHeaderColumn().run();
            handleMenuClose();
          }}
          disabled={!editor.can().toggleHeaderColumn()}
        >
          Toggle Header Column
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().toggleHeaderRow().run();
            handleMenuClose();
          }}
          disabled={!editor.can().toggleHeaderRow()}
        >
          Toggle Header Row
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().toggleHeaderCell().run();
            handleMenuClose();
          }}
          disabled={!editor.can().toggleHeaderCell()}
        >
          Toggle Header Cell
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().mergeOrSplit().run();
            handleMenuClose();
          }}
          disabled={!editor.can().mergeOrSplit()}
        >
          Merge or Split
        </MenuItem>
      </Menu>
      <Tooltip title="Set Link">
        <IconButton
          onClick={setLink}
        >
          <AddLinkIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Embed Youtube Video">
        <IconButton
          onClick={addYoutubeVideo}
        >
          <YouTubeIcon />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default MenuBar;
